import { useState, useCallback, useRef } from "react";
import { UserIdentifier } from "@preveil-api";
import { Helpers, getServicePasswordValidation, getHIBPValidationList, AccountErrorMessages, StatusCode } from "src/common";
import _ from "lodash";


const HIBP_HASH_LENGTH = 5;
const HIBP_RETRIES = 3;
export function usePasswordRemoteValidation(user_identifier?: UserIdentifier) {
    const [validated, setValidated] = useState<string | undefined>();
    const [error, setError] = useState<string | undefined>();

    const retry_count_ref = useRef<number>(0);
    function setRetryCount(_retry: number) {
        retry_count_ref.current = _retry;
    }
    // Description: Check against compromised passwords https://haveibeenpwned.com/Passwords
    // https://haveibeenpwned.com/API/v3#ResponseCodes (SERVICE_UNAVAILABLE and TOO_MANY_REQUESTS will trigger retries)
    async function validateHIBP(password: string) {
        try {
            // NOTE: Hash the password
            const hash = await Helpers.sha1String(password);

            // NOTE: Send only first 5 characters (k-anonymity)
            const prefix = hash.substring(0, HIBP_HASH_LENGTH);
            const suffix = hash.substring(HIBP_HASH_LENGTH);
            const response = await getHIBPValidationList(prefix);
            if (!response.isError && !!response.data) {
                const text = response.data;

                // NOTE: Parse response - format is "SUFFIX:COUNT\r\n"
                const hashes = text.split("\r\n");
                const found = hashes.find(line => {
                    const [hash_suffix] = line.split(":");
                    return hash_suffix === suffix;
                });
                const count = !!found ? parseInt(found.split(":")[1], 10) : 0;
                !count ?
                    validateContextWords(password) : handleInvalidPassword(password);
            } else {
                // NOTE: error fetching the validation hashes If it is a 429 }|| 503 then retry a calls a few times (HIBP_RETRIES)
                if ((response.status === StatusCode.SERVICE_UNAVAILABLE || response.status === StatusCode.TOO_MANY_REQUESTS)
                    && retry_count_ref.current < HIBP_RETRIES) {
                    setRetryCount(Number(retry_count_ref.current + 1));
                    // NOTE: Exponential backoff for retry delay
                    const delay = Math.pow(2, retry_count_ref.current) * 500;
                    setTimeout(() => {
                        validateHIBP(password);
                    }, delay);
                } else {
                    // NOTE: Let the password go through and we will validate again on login
                    validateContextWords(password);
                }
            }
        } catch (err: unknown) {
            // NOTE: Let the password go through and we will validate again on login
            validateContextWords(password);
        }
    }

    // Description: Check against a list of context words
    async function validateContextWords(password: string) {
        const response = await getServicePasswordValidation();
        if (!response.isError) {
            const context_words = response.data.context_words;
            if (!!context_words && Array.isArray(context_words) && context_words.length > 0) {
                const count = _.filter(context_words, (word: string) => {
                    return password.toLowerCase().includes(word.toLowerCase());
                }).length;
                !count ?
                    validateUserInformation(password) : handleInvalidPassword(password);
            } else {
                // NOTE: Let the password go through and we will validate again on login
                validateUserInformation(password);
            }
        } else {    // NOTE: Let the password go through and we will validate again on login
            validateUserInformation(password);
        }
    }

    // Description: Check against user specific information like username, name, last name (optional as data may not be available)
    function validateUserInformation(password: string) {
        if (!!user_identifier) {
            const _pass = password.toLowerCase();
            const email = user_identifier.user_id.toLowerCase();
            // NOTE: Success! It passed all three remote validations
            !_pass.includes(email) ?
                setValidated(password) : handleInvalidPassword(password);
        } else {
            // NOTE: Success! It passed all three remote validations
            setValidated(password);
        }
    }

    // Description: Handle Error messages
    // Parameter:  set_warning means that the calls to HIBP were unsuccessful and password IS not safe (invalid)
    // error_message: pass a custom error message
    // Otherwise, calls failed for other reasons: services interruption or others
    function handleInvalidPassword(password: string, error_message: string = AccountErrorMessages.create_remote_password_validation) {
        setError(error_message);
    }

    // Description: Callback for validating password remotely
    const validatePassword = useCallback((password: string) => {
        validateHIBP(password);
    }, []);

    // Description: Reset hook state 
    const resetValidation = useCallback(() => {
        setValidated(undefined);
        setError(undefined);
        setRetryCount(0);
    }, []);

    return {
        validatePassword,
        resetValidation,
        validated,
        error
    };
}